<script setup>
/* eslint-disable no-await-in-loop, vue/no-bare-strings-in-template */
import { ref, computed, onMounted, nextTick, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import { useConfig } from 'dashboard/composables/useConfig';
import { emitter } from 'shared/helpers/mitt';
import { MESSAGE_VARIABLES } from 'shared/constants/messages';
import MessageAPI from 'dashboard/api/inbox/message';
import ConversationAPI from 'dashboard/api/inbox/conversation';
import ContactsAPI from 'dashboard/api/contacts';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import TimeAgo from 'components/ui/TimeAgo.vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import TemplatesPicker from 'dashboard/components/widgets/conversation/WhatsappTemplates/TemplatesPicker.vue';
import WhatsAppTemplateParser from 'dashboard/components-next/whatsapp/WhatsAppTemplateParser.vue';

const PER_PAGE = 25;
const STEP_COMPLETED_NUMBER_BG = '#10b981';
const store = useStore();
const { t } = useI18n();
const { isStacklab } = useConfig();

const loadingList = ref(false);
const listFetchId = ref(0);
const sendingMessage = ref(false);
const conversations = ref([]);
const totalConversations = ref(0);
const conversationPage = ref(1);
const wizardStep = ref(1);
const selectedSource = ref('');
const selectedDispatchInboxId = ref('');
const sourceLabel = ref('');
const sourceInboxId = ref('');
const sourceFunnelId = ref('');
const sourceStage = ref('');
const csvFileName = ref('');
const csvHeaders = ref([]);
const csvRows = ref([]);
const csvPreviewRows = ref([]);
const csvMapping = ref({
  name: '',
  email: '',
  phone_number: '',
  identifier: '',
});

const matchedCsvConversationIds = computed(() =>
  csvPreviewRows.value
    .map(item => item.conversation?.id)
    .filter(Boolean)
);

const allCsvRowsSelected = computed(
  () =>
    matchedCsvConversationIds.value.length > 0 &&
    matchedCsvConversationIds.value.every(id => selectedIds.value.has(id))
);
const composerMessage = ref('');
const selectedWhatsappTemplate = ref(null);

const selectedIds = ref(new Set());
const selectedCsvRows = ref(new Set());
const sendNow = ref(true);
const scheduledTime = ref('');
const messageVariations = ref([]);
const editingVariationIndex = ref(null);
const variationInput = ref('');

const customAttributes = useMapGetter('attributes/getAttributes');
const labels = useMapGetter('labels/getLabels');
const inboxes = useMapGetter('inboxes/getInboxes');
const funnels = useMapGetter('funnel/getFunnels');

const sourceOptions = computed(() => [
  { id: 'csv', icon: 'i-lucide-file-spreadsheet' },
  { id: 'label', icon: 'i-lucide-tag' },
  { id: 'inbox', icon: 'i-lucide-inbox' },
  { id: 'kanban', icon: 'i-lucide-columns-3' },
]);

const selectedFunnel = computed(() =>
  funnels.value.find(funnel => String(funnel.id) === sourceFunnelId.value)
);

const selectedDispatchInbox = computed(() =>
  inboxes.value.find(inbox => String(inbox.id) === selectedDispatchInboxId.value)
);

const funnelStages = computed(() =>
  Object.entries(selectedFunnel.value?.stages || {})
    .map(([id, stage]) => ({ id, ...stage }))
    .sort((a, b) => (a.position || 0) - (b.position || 0))
);

const canLoadSource = computed(() => {
  if (selectedSource.value === 'csv') {
    return (
      csvRows.value.length > 0 &&
      Boolean(
        csvMapping.value.email ||
          csvMapping.value.phone_number ||
          csvMapping.value.identifier
      )
    );
  }
  if (selectedSource.value === 'label') return Boolean(sourceLabel.value);
  if (selectedSource.value === 'inbox') return Boolean(sourceInboxId.value);
  if (selectedSource.value === 'kanban') {
    return Boolean(sourceFunnelId.value && sourceStage.value);
  }
  return false;
});

const variablesDialogRef = useTemplateRef('variablesDialogRef');
const filtersDialogRef = useTemplateRef('filtersDialogRef');
const whatsappTemplateParserRef = useTemplateRef('whatsappTemplateParserRef');

const conversationFilters = ref({ query: '', channel: '', label: '', priority: '' });

const openVariablesModal = () => {
  nextTick(() => {
    variablesDialogRef.value?.open();
  });
};

const closeFiltersModal = () => filtersDialogRef.value?.close();
const clearConversationFilters = () => {
  conversationFilters.value = { query: '', channel: '', label: '', priority: '' };
};
const channelFilterOptions = computed(() => []);
const labelFilterOptions = computed(() => []);
const priorityFilterOptions = computed(() => []);
const filteredConversations = computed(() => conversations.value);


const standardVariablesList = computed(() =>
  MESSAGE_VARIABLES.map(v => ({
    key: v.key,
    description: v.label,
  }))
);

const customVariablesList = computed(() =>
  (customAttributes.value || []).map(attribute => {
    const model = attribute.attribute_model ?? attribute.attributeModel ?? '';
    const prefix =
      model === 'conversation_attribute' ? 'conversation' : 'contact';
    const attrKey = attribute.attribute_key ?? attribute.attributeKey ?? '';
    const key = `${prefix}.custom_attribute.${attrKey}`;
    const description =
      attribute.attribute_description ?? attribute.attributeDescription ?? key;
    return { key, description };
  })
);

const isProcessing = ref(false);
const processedItems = ref([]);
const successCount = ref(0);
const errorCount = ref(0);
const progress = ref(0);

const plainTextFromHtml = html => {
  if (!html) return '';
  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const isComposerEmpty = computed(
  () => plainTextFromHtml(composerMessage.value) === ''
);

const isMessageBodyBlank = body => plainTextFromHtml(body) === '';

const conversationTitle = c => {
  const name = c.meta?.sender?.name;
  return name || `#${c.id}`;
};

const conversationInitial = c => {
  const title = conversationTitle(c);
  return title ? title.charAt(0).toUpperCase() : '#';
};

const conversationThumbnail = c => c.meta?.sender?.thumbnail;

const conversationLabels = c => (Array.isArray(c.labels) ? c.labels : []);

const priorityClasses = priority => {
  const classes = {
    urgent: 'bg-ruby-100 text-ruby-700 dark:bg-ruby-900/40 dark:text-ruby-300',
    high: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
    medium: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
    low: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  };
  return classes[priority] || classes.low;
};

const priorityLabel = priority =>
  t(`KANBAN.PRIORITY_LABELS.${priority.toUpperCase()}`);

const conversationInboxName = c => {
  const inboxName = c.inbox?.name || c.inbox_name || c.inboxName;
  if (inboxName) return inboxName;

  const inbox = store.getters['inboxes/getInbox']?.(c.inbox_id);
  return inbox?.name || '—';
};

const uniqueOptions = list => [...new Set(list.filter(Boolean))].sort();


const conversationPageCount = computed(() =>
  Math.max(1, Math.ceil(totalConversations.value / PER_PAGE))
);

const paginatedConversations = computed(() => conversations.value);

const hasSelections = computed(
  () => selectedIds.value.size > 0 && conversations.value.length > 0
);

const selectedConversations = computed(() =>
  conversations.value.filter(c => selectedIds.value.has(c.id))
);

const inboxIdForConversation = conversation =>
  conversation.inbox_id || conversation.inboxId || conversation.inbox?.id;

const inboxForConversation = conversation => {
  const inboxId = inboxIdForConversation(conversation);
  const storeInbox = inboxId
    ? store.getters['inboxes/getInbox']?.(inboxId)
    : {};

  return (
    (storeInbox && Object.keys(storeInbox).length ? storeInbox : null) ||
    conversation.inbox ||
    {}
  );
};

const isWhatsappCloudConversation = conversation => {
  const inbox = inboxForConversation(conversation);
  const channelType =
    inbox.channel_type || inbox.channelType || conversation.channel_type;
  const provider =
    inbox.provider ||
    inbox.channel?.provider ||
    inbox.additional_attributes?.provider ||
    inbox.additionalAttributes?.provider ||
    conversation.inbox?.provider;
  const inboxName = (
    inbox.name ||
    conversation.inbox?.name ||
    ''
  ).toLowerCase();
  const isWhatsappWeb =
    channelType === 'Channel::WhatsappWeb' ||
    provider === 'whatsapp_web' ||
    inboxName.includes('whatsapp web');

  return (
    !isWhatsappWeb &&
    (channelType === 'Channel::Whatsapp' ||
      provider === 'whatsapp_cloud' ||
      inboxName.includes('whatsapp'))
  );
};

const selectedWhatsappCloudConversations = computed(() =>
  selectedConversations.value.filter(isWhatsappCloudConversation)
);

const selectedNonWhatsappCloudConversations = computed(() =>
  selectedConversations.value.filter(c => !isWhatsappCloudConversation(c))
);

const hasSelectedWhatsappCloud = computed(
  () => selectedWhatsappCloudConversations.value.length > 0
);

const hasSelectedNonWhatsappCloud = computed(
  () => selectedNonWhatsappCloudConversations.value.length > 0
);

const whatsappCloudInboxId = computed(() =>
  inboxIdForConversation(selectedWhatsappCloudConversations.value[0] || {})
);

const allListedSelected = computed(
  () =>
    paginatedConversations.value.length > 0 &&
    paginatedConversations.value.every(c => selectedIds.value.has(c.id))
);

const messageBodies = computed(() => {
  const bodies = [];
  if (!isComposerEmpty.value) bodies.push(composerMessage.value);
  bodies.push(
    ...messageVariations.value.filter(body => !isMessageBodyBlank(body))
  );
  return bodies;
});

const canSendMessages = computed(
  () =>
    (!hasSelectedNonWhatsappCloud.value || messageBodies.value.length > 0) &&
    (!hasSelectedWhatsappCloud.value || selectedWhatsappTemplate.value) &&
    hasSelections.value &&
    (sendNow.value ||
      (!hasSelectedWhatsappCloud.value && scheduledTime.value.trim() !== ''))
);

const step1Done = computed(() => hasSelections.value);
const step2Done = computed(
  () =>
    (!hasSelectedNonWhatsappCloud.value || messageBodies.value.length > 0) &&
    (!hasSelectedWhatsappCloud.value || selectedWhatsappTemplate.value)
);
const step3Done = computed(
  () => sendNow.value || scheduledTime.value.trim() !== ''
);

const highlightedStep = computed(() => {
  if (!step1Done.value) return 1;
  if (!step2Done.value) return 2;
  if (!step3Done.value) return 3;
  return null;
});

const isCompleted = computed(
  () => progress.value === 100 && !sendingMessage.value
);

const buildListParams = page => ({ page, status: 'open' });

const fetchAllConversations = async params => {
  const records = [];
  let page = 1;
  let total = 0;

  do {
    const response = await ConversationAPI.get({ ...params, page });
    const payload = response.data?.data?.payload ?? [];
    total = response.data?.data?.meta?.all_count ?? payload.length;
    records.push(...payload);
    page += 1;
  } while (records.length < total);

  return records;
};

const normalizeIdentifier = value =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9@+.]/g, '');

const conversationMatchesCsvRow = (conversation, row) => {
  const sender = conversation.meta?.sender || {};
  return ['email', 'phone_number', 'identifier'].some(field => {
    const column = csvMapping.value[field];
    return (
      column &&
      normalizeIdentifier(row[column]) === normalizeIdentifier(sender[field])
    );
  });
};

const conversationMatchesKanban = conversation =>
  (conversation.kanban_items || []).some(
    item =>
      String(item.funnel_id) === sourceFunnelId.value &&
      String(item.funnel_stage) === sourceStage.value
  );

const handleCsvFile = async event => {
  const [file] = event.target.files || [];
  csvFileName.value = file?.name || '';
  csvHeaders.value = [];
  csvRows.value = [];
  csvPreviewRows.value = [];
  csvMapping.value = { name: '', email: '', phone_number: '', identifier: '' };
  if (!file) return;

  const content = await file.text();
  const lines = content.split(/\r?\n/).filter(Boolean);
  const delimiter =
    (lines[0]?.match(/;/g) || []).length > (lines[0]?.match(/,/g) || []).length
      ? ';'
      : ',';
  csvHeaders.value = (lines.shift() || '')
    .split(delimiter)
    .map(value => value.trim());
  csvRows.value = lines.map(line =>
    Object.fromEntries(
      line
        .split(delimiter)
        .map((value, index) => [csvHeaders.value[index], value.trim()])
    )
  );

  const normalizedHeaders = Object.fromEntries(
    csvHeaders.value.map(header => [normalizeIdentifier(header), header])
  );
  csvMapping.value = {
    name: normalizedHeaders.name || normalizedHeaders.nome || '',
    email: normalizedHeaders.email || normalizedHeaders.emailaddress || '',
    phone_number:
      normalizedHeaders.phone ||
      normalizedHeaders.telefone ||
      normalizedHeaders.phonenumber ||
      '',
    identifier:
      normalizedHeaders.identifier || normalizedHeaders.identificador || '',
  };
};

const loadSourceRecipients = async () => {
  if (!canLoadSource.value) return;
  if (selectedSource.value === 'inbox') {
    selectedDispatchInboxId.value = sourceInboxId.value;
  }
  loadingList.value = true;
  try {
    const params = { status: 'all' };
    if (selectedDispatchInboxId.value) {
      params.inbox_id = selectedDispatchInboxId.value;
    }
    if (selectedSource.value === 'label') params.labels = [sourceLabel.value];
    if (selectedSource.value === 'inbox') params.inbox_id = sourceInboxId.value;

    let records = await fetchAllConversations(params);
    if (selectedSource.value === 'csv') {
      csvPreviewRows.value = csvRows.value.map((row, index) => {
        const conversation = records.find(record =>
          conversationMatchesCsvRow(record, row)
        );
        return { index, row, conversation };
      });
      selectedCsvRows.value = new Set(csvPreviewRows.value.map(item => item.index));
      records = csvPreviewRows.value
        .map(item => item.conversation || {
          id: `csv-${item.index}`,
          inbox_id: selectedDispatchInboxId.value || inboxes.value[0]?.id,
          csvRow: item.row,
          csvIndex: item.index,
          meta: { sender: { name: item.row[csvMapping.value.name] } },
        })
        .filter(
          (conversation, index, list) =>
            list.findIndex(item => item.id === conversation.id) === index &&
            conversation.inbox_id
        );
    }
    if (selectedSource.value === 'kanban') {
      records = records.filter(conversationMatchesKanban);
    }

    conversations.value = records;
    totalConversations.value = records.length;
    selectedIds.value = new Set(records.map(conversation => conversation.id));
    conversationPage.value = 1;
    wizardStep.value = 2;
  } catch (error) {
    emitter.emit('newToastMessage', {
      message: error?.message || t('CONVERSATION.FORWARD_MESSAGE.ERROR'),
      type: 'error',
    });
  } finally {
    loadingList.value = false;
  }
};

const handleDispatchInboxChange = () => {
  if (wizardStep.value === 2 && selectedSource.value !== 'inbox') {
    loadSourceRecipients();
  }
};

const changeSource = () => {
  wizardStep.value = 1;
  conversations.value = [];
  selectedIds.value = new Set();
  totalConversations.value = 0;
};

const fetchConversations = async (page = 1) => {
  const currentFetchId = listFetchId.value + 1;
  listFetchId.value = currentFetchId;
  loadingList.value = true;
  selectedIds.value = new Set();
  conversations.value = [];
  try {
    const response = await ConversationAPI.get(buildListParams(page));
    if (currentFetchId !== listFetchId.value) return;

    const payload = response.data?.data?.payload ?? [];
    const meta = response.data?.data?.meta ?? {};
    conversations.value = payload;
    conversationPage.value = page;
    totalConversations.value = meta.all_count ?? payload.length;
  } catch (error) {
    conversations.value = [];
    totalConversations.value = 0;
    emitter.emit('newToastMessage', {
      message: error?.message || t('CONVERSATION.FORWARD_MESSAGE.ERROR'),
      type: 'error',
    });
  } finally {
    if (currentFetchId === listFetchId.value) {
      loadingList.value = false;
    }
  }
};

const toggleId = id => {
  const next = new Set(selectedIds.value);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  selectedIds.value = next;
};

const selectAllListed = () => {
  const visibleIds = paginatedConversations.value.map(c => c.id);
  if (allListedSelected.value) {
    const next = new Set(selectedIds.value);
    visibleIds.forEach(id => next.delete(id));
    selectedIds.value = next;
  } else {
    selectedIds.value = new Set([...selectedIds.value, ...visibleIds]);
  }
};

const selectAllCsvRows = () => {
  const ids = matchedCsvConversationIds.value;
  const rows = csvPreviewRows.value.map(item => item.index);
  if (allCsvRowsSelected.value) {
    const next = new Set(selectedIds.value);
    ids.forEach(id => next.delete(id));
    selectedIds.value = next;
    selectedCsvRows.value = new Set();
  } else {
    selectedIds.value = new Set([...selectedIds.value, ...ids]);
    selectedCsvRows.value = new Set(rows);
  }
};

const toggleCsvRow = item => {
  const nextRows = new Set(selectedCsvRows.value);
  if (nextRows.has(item.index)) nextRows.delete(item.index);
  else nextRows.add(item.index);
  selectedCsvRows.value = nextRows;
  toggleId(item.conversation?.id || `csv-${item.index}`);
};

const previousConversationPage = () => {
  if (conversationPage.value <= 1 || loadingList.value) return;
  fetchConversations(conversationPage.value - 1);
};

const nextConversationPage = () => {
  if (
    conversationPage.value >= conversationPageCount.value ||
    loadingList.value
  ) {
    return;
  }
  fetchConversations(conversationPage.value + 1);
};

const handleAddVariation = () => {
  editingVariationIndex.value = messageVariations.value.length;
  variationInput.value = composerMessage.value;
};

const handleSaveVariation = () => {
  if (isMessageBodyBlank(variationInput.value)) return;
  if (
    editingVariationIndex.value !== null &&
    editingVariationIndex.value < messageVariations.value.length
  ) {
    messageVariations.value[editingVariationIndex.value] = variationInput.value;
  } else {
    messageVariations.value.push(variationInput.value);
  }
  editingVariationIndex.value = null;
  variationInput.value = '';
};

const handleCancelVariation = () => {
  editingVariationIndex.value = null;
  variationInput.value = '';
};

const handleEditVariation = index => {
  editingVariationIndex.value = index;
  variationInput.value = messageVariations.value[index];
};

const handleRemoveVariation = index => {
  messageVariations.value.splice(index, 1);
};

const handleSelectWhatsappTemplate = template => {
  selectedWhatsappTemplate.value = template;
};

const handleResetWhatsappTemplate = () => {
  selectedWhatsappTemplate.value = null;
};

const buildWhatsappTemplatePayload = () => {
  const parser = whatsappTemplateParserRef.value;
  if (!selectedWhatsappTemplate.value || !parser) return null;

  parser.v$?.$touch?.();
  if (parser.v$?.$invalid) return null;

  const processedParams =
    parser.processedParams?.value || parser.processedParams || {};
  const message = parser.renderedTemplate?.value || parser.renderedTemplate;
  const { name, category, language, namespace } =
    selectedWhatsappTemplate.value;

  return {
    message,
    templateParams: {
      name,
      category,
      language,
      namespace,
      processed_params: processedParams,
    },
  };
};

const handleSend = async () => {
  if (!canSendMessages.value) return;

  const bodies = messageBodies.value;
  if (hasSelectedNonWhatsappCloud.value && bodies.length === 0) return;

  const whatsappTemplatePayload = hasSelectedWhatsappCloud.value
    ? buildWhatsappTemplatePayload()
    : null;
  if (hasSelectedWhatsappCloud.value && !whatsappTemplatePayload) return;

  const targets = selectedConversations.value;

  isProcessing.value = true;
  sendingMessage.value = true;
  processedItems.value = [];
  successCount.value = 0;
  errorCount.value = 0;
  progress.value = 0;

  try {
    for (let i = 0; i < targets.length; i += 1) {
      const c = targets[i];
      try {
        let conversation = c;
        if (c.csvRow) {
          const contactResponse = await ContactsAPI.create({
            name: c.csvRow[csvMapping.value.name],
            email: c.csvRow[csvMapping.value.email],
            phone_number: c.csvRow[csvMapping.value.phone_number],
            identifier: c.csvRow[csvMapping.value.identifier],
          });
          const contact = contactResponse.data.payload.contact;
          const conversationResponse = await ConversationAPI.create({
            contact_id: contact.id,
            inbox_id: c.inbox_id,
          });
          conversation = conversationResponse.data;
        }
        const inboxId = selectedDispatchInboxId.value || conversation.inbox_id;
        const isWhatsappCloud = isWhatsappCloudConversation(c);
        const messageContent = isWhatsappCloud
          ? whatsappTemplatePayload.message
          : bodies[i % bodies.length];
        if (sendNow.value) {
          await MessageAPI.create({
            conversationId: conversation.id,
            message: messageContent,
            private: false,
            message_type: 'outgoing',
            inbox_id: inboxId,
            templateParams: isWhatsappCloud
              ? whatsappTemplatePayload.templateParams
              : undefined,
          });
        } else {
          const plain = plainTextFromHtml(messageContent);
          const title =
            plain.length > 50 ? `${plain.slice(0, 50)}…` : plain || '…';
          await ConversationAPI.scheduleMessage({
            conversation_id: conversation.id,
            inbox_id: inboxId,
            content: messageContent,
            scheduled_at: new Date(scheduledTime.value).toISOString(),
            title,
          });
        }
        processedItems.value.push({
          id: conversation.id,
          title: conversationTitle(conversation),
          conversationId: conversation.id,
          status: 'success',
        });
        successCount.value += 1;
      } catch (error) {
        processedItems.value.push({
          id: c.id,
          title: conversationTitle(c),
          conversationId: c.id,
          status: 'error',
          error: error?.message,
        });
        errorCount.value += 1;
      }
      progress.value = ((i + 1) / targets.length) * 100;
    }

    emitter.emit('newToastMessage', {
      message: sendNow.value
        ? t('MESSAGE_DISPATCHER.TOAST_SEND_DONE', {
            ok: successCount.value,
            fail: errorCount.value,
          })
        : t('MESSAGE_DISPATCHER.TOAST_SCHEDULE_DONE', {
            ok: successCount.value,
            fail: errorCount.value,
          }),
      type: successCount.value > 0 ? 'success' : 'error',
    });
  } catch {
    emitter.emit('newToastMessage', {
      message: t('CONVERSATION.FORWARD_MESSAGE.ERROR'),
      type: 'error',
    });
  } finally {
    sendingMessage.value = false;
  }
};

const finishProgress = () => {
  isProcessing.value = false;
  processedItems.value = [];
  successCount.value = 0;
  errorCount.value = 0;
  progress.value = 0;
};

onMounted(() => {
  if (!isStacklab) return;

  store.dispatch('attributes/get');
  store.dispatch('inboxes/get');
  store.dispatch('labels/get');
  store.dispatch('funnel/fetch');
});
</script>

<template>
  <div
    v-if="!isStacklab"
    class="flex h-full w-full items-center justify-center bg-n-surface-2"
  >
    <div class="mx-auto max-w-md p-6 text-center">
      <span class="i-lucide-lock mb-4 inline-block size-12 text-n-slate-10" />
      <h2 class="mb-2 text-xl font-semibold text-n-slate-12">
        Licença Necessária
      </h2>
      <p class="text-sm text-n-slate-11">
        Esta instalação não possui uma licença válida para acessar o Disparador
        de Mensagens.
        Entre em contato com o administrador da plataforma.
      </p>
    </div>
  </div>
  <div
    v-else
    class="flex h-full w-full min-h-0 flex-col overflow-hidden bg-n-background"
  >
    <header
      class="mx-auto flex w-full max-w-[96rem] shrink-0 flex-col gap-4 px-6 pt-6 sm:flex-row sm:items-start sm:justify-between lg:px-8 lg:pt-8"
    >
      <div class="flex flex-col gap-1">
        <h1 class="text-2xl font-semibold text-n-slate-12">
          {{ t('MESSAGE_DISPATCHER.TITLE') }}
        </h1>
        <p class="max-w-2xl text-sm text-n-slate-11">
          {{ t('MESSAGE_DISPATCHER.DESCRIPTION') }}
        </p>
      </div>
      <div
        v-if="!isProcessing && wizardStep === 2"
        class="flex flex-wrap items-center justify-end gap-4"
      >
        <Button
          variant="ghost"
          color="slate"
          size="sm"
          icon="i-lucide-arrow-left"
          @click="changeSource"
        >
          {{ t('MESSAGE_DISPATCHER.SOURCE.CHANGE') }}
        </Button>
        <div class="flex items-center gap-3">
          <span
            class="text-sm font-medium text-n-slate-12"
            :class="{ 'text-n-slate-10': sendNow }"
          >
            {{ t('MESSAGE_DISPATCHER.TOGGLE_SCHEDULE_LATER') }}
          </span>
          <Switch v-model="sendNow" />
          <span
            class="text-sm font-medium text-n-slate-12"
            :class="{ 'text-n-slate-10': !sendNow }"
          >
            {{ t('MESSAGE_DISPATCHER.TOGGLE_SCHEDULE_NOW') }}
          </span>
        </div>
        <Button
          variant="solid"
          color="blue"
          size="sm"
          :is-loading="sendingMessage"
          :disabled="!canSendMessages"
          @click="handleSend"
        >
          {{ t('KANBAN.BULK_ACTIONS.SEND_MESSAGES') }}
        </Button>
      </div>
    </header>

    <div
      class="mx-auto flex min-h-0 w-full max-w-[96rem] flex-1 flex-col overflow-hidden px-6 pb-6 pt-6 lg:px-8 lg:pb-8"
    >
      <div v-if="isProcessing" class="min-h-0 flex-1 space-y-6 overflow-y-auto">
        <div
          class="rounded-2xl border border-n-weak bg-n-solid-1 p-6 shadow-sm"
        >
          <div class="text-center space-y-2">
            <p
              class="text-xs font-semibold uppercase tracking-wide"
              :class="isCompleted ? 'text-emerald-600' : 'text-n-brand'"
            >
              {{
                isCompleted
                  ? t('KANBAN.BULK_ACTIONS.SEND_MESSAGE.COMPLETED_BADGE')
                  : t('KANBAN.BULK_ACTIONS.SEND_MESSAGE.PROCESSING_BADGE')
              }}
            </p>
            <h3
              class="text-xl font-semibold text-n-slate-12"
              :class="isCompleted ? 'text-emerald-600' : ''"
            >
              {{
                isCompleted
                  ? t('MESSAGE_DISPATCHER.DONE_TITLE')
                  : t('MESSAGE_DISPATCHER.PROGRESS_TITLE')
              }}
            </h3>
            <p class="text-sm text-n-slate-11">
              {{
                t('MESSAGE_DISPATCHER.PROGRESS_HINT', {
                  count: processedItems.length,
                  total: selectedIds.size,
                })
              }}
            </p>
          </div>
          <div class="relative mt-6 space-y-3">
            <div
              class="flex items-center justify-between text-xs font-semibold"
            >
              <span
                class="inline-flex items-center rounded-full px-3 py-1"
                :class="
                  isCompleted
                    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                    : 'bg-n-alpha-2 text-n-brand'
                "
              >
                {{ Math.round(progress) }}%
              </span>
              <span class="text-n-slate-11">
                {{ successCount }}
                {{ t('KANBAN.BULK_ACTIONS.SEND_MESSAGE.SUCCESS') }} ·
                {{ errorCount }}
                {{ t('KANBAN.BULK_ACTIONS.SEND_MESSAGE.ERROR') }}
              </span>
            </div>
            <div class="h-3 overflow-hidden rounded-full bg-n-alpha-2">
              <div
                class="h-full rounded-full transition-all duration-500"
                :class="isCompleted ? 'bg-emerald-500' : 'bg-n-brand'"
                :style="{ width: `${progress}%` }"
              />
            </div>
          </div>
        </div>

        <div
          class="rounded-2xl border border-n-weak bg-n-solid-1 shadow-sm overflow-hidden"
        >
          <div
            class="flex items-center justify-between border-b border-n-weak px-6 py-4 text-sm"
          >
            <div class="font-medium text-n-slate-12">
              {{ t('KANBAN.BULK_ACTIONS.SEND_MESSAGE.PROCESSING_TITLE') }}
            </div>
            <span class="text-xs text-n-slate-11">
              {{
                t('KANBAN.BULK_ACTIONS.SEND_MESSAGE.PROCESSING_RESULTS', {
                  count: processedItems.length,
                })
              }}
            </span>
          </div>
          <div class="max-h-[430px] divide-y divide-n-weak overflow-y-auto">
            <div
              v-for="item in processedItems"
              :key="item.id"
              class="flex items-center gap-3 px-6 py-4 text-sm hover:bg-n-alpha-1"
            >
              <span
                class="flex h-10 w-10 items-center justify-center rounded-full"
                :class="
                  item.status === 'success'
                    ? 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/40'
                    : 'bg-ruby-100 text-ruby-600 dark:bg-ruby-900/40'
                "
              >
                <fluent-icon
                  :icon="
                    item.status === 'success'
                      ? 'checkmark-circle'
                      : 'error-circle'
                  "
                  size="18"
                />
              </span>
              <div class="flex-1 min-w-0">
                <p class="font-medium text-n-slate-12 truncate">
                  {{ item.title }}
                </p>
                <p class="text-xs text-n-slate-11">
                  {{
                    t('KANBAN.BULK_ACTIONS.SEND_MESSAGE.CONVERSATION_ID', {
                      id: item.conversationId,
                    })
                  }}
                </p>
                <p v-if="item.error" class="mt-2 text-xs text-ruby-600">
                  {{ item.error }}
                </p>
              </div>
              <span
                class="rounded-full px-3 py-1 text-xs font-semibold shrink-0"
                :class="
                  item.status === 'success'
                    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                    : 'bg-ruby-100 text-ruby-700 dark:bg-ruby-900/40 dark:text-ruby-300'
                "
              >
                {{
                  item.status === 'success'
                    ? t('KANBAN.BULK_ACTIONS.SEND_MESSAGE.STATUS_SUCCESS')
                    : t('KANBAN.BULK_ACTIONS.SEND_MESSAGE.STATUS_ERROR')
                }}
              </span>
            </div>
          </div>
        </div>

        <div class="flex justify-end">
          <Button
            variant="ghost"
            color="slate"
            size="sm"
            @click="finishProgress"
          >
            {{ t('MESSAGE_DISPATCHER.CLOSE') }}
          </Button>
        </div>
      </div>

      <section
        v-else-if="wizardStep === 1"
        class="mx-auto flex w-full max-w-[96rem] flex-1 flex-col overflow-y-auto py-3 md:py-4"
      >
        <div class="mb-4 flex items-center gap-2 text-sm text-n-slate-11">
          <span
            class="flex h-7 w-7 items-center justify-center rounded-full bg-n-brand font-semibold text-white"
            >1</span
          >
          <span class="font-medium text-n-slate-12">
            {{ t('MESSAGE_DISPATCHER.SOURCE.STEP') }}
          </span>
          <span class="h-px flex-1 bg-n-weak" />
          <span
            class="flex h-7 w-7 items-center justify-center rounded-full bg-n-alpha-2 font-semibold"
            >2</span
          >
          <span>{{ t('MESSAGE_DISPATCHER.MESSAGE_SECTION') }}</span>
          <span class="h-px flex-1 bg-n-weak" />
          <span
            class="flex h-7 w-7 items-center justify-center rounded-full bg-n-alpha-2 font-semibold"
            >3</span
          >
          <span>{{ t('MESSAGE_DISPATCHER.SOURCE.REVIEW') }}</span>
        </div>

        <div class="mb-4 max-w-2xl">
          <h2 class="mb-2 text-2xl font-semibold text-n-slate-12">
            {{ t('MESSAGE_DISPATCHER.SOURCE.TITLE') }}
          </h2>
          <p class="text-sm leading-6 text-n-slate-11">
            {{ t('MESSAGE_DISPATCHER.SOURCE.DESCRIPTION') }}
          </p>
        </div>

        <div class="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <button
            v-for="source in sourceOptions"
            :key="source.id"
            type="button"
            class="flex items-start gap-3 rounded-xl border bg-n-solid-1 p-4 text-left transition-colors hover:bg-n-alpha-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
            :class="
              selectedSource === source.id
                ? 'border-2 border-n-brand p-[15px]'
                : 'border-n-weak'
            "
            @click="selectedSource = source.id"
          >
            <span
              class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-n-alpha-2 text-n-brand"
            >
              <span :class="source.icon" class="text-xl" />
            </span>
            <span class="min-w-0">
              <span class="block text-base font-semibold text-n-slate-12">
                {{
                  t(
                    `MESSAGE_DISPATCHER.SOURCE.${source.id.toUpperCase()}.TITLE`
                  )
                }}
              </span>
              <span class="mt-1 block text-sm leading-5 text-n-slate-11">
                {{
                  t(
                    `MESSAGE_DISPATCHER.SOURCE.${source.id.toUpperCase()}.DESCRIPTION`
                  )
                }}
              </span>
            </span>
            <span
              class="ml-auto mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border"
              :class="
                selectedSource === source.id
                  ? 'border-n-brand bg-n-brand text-white'
                  : 'border-n-strong'
              "
            >
              <span
                v-if="selectedSource === source.id"
                class="i-lucide-check text-xs"
              />
            </span>
          </button>
        </div>

        <div
          v-if="selectedSource"
          class="mt-4 rounded-xl border border-n-weak bg-n-solid-1 p-4"
        >
          <div v-if="selectedSource === 'csv'" class="block">
            <span class="mb-2 block text-sm font-medium text-n-slate-12">
              {{ t('MESSAGE_DISPATCHER.SOURCE.CSV.FIELD') }}
            </span>
            <input
              type="file"
              accept=".csv,text/csv"
              class="block w-full rounded-lg border border-n-weak bg-n-solid-1 p-3 text-sm text-n-slate-12 file:mr-3 file:rounded-md file:border-0 file:bg-n-alpha-2 file:px-3 file:py-2 file:font-medium file:text-n-slate-12"
              @change="handleCsvFile"
            />
            <span class="mt-2 block text-xs text-n-slate-11">
              {{
                csvFileName
                  ? t('MESSAGE_DISPATCHER.SOURCE.CSV.LOADED', {
                      count: csvRows.length,
                    })
                  : t('MESSAGE_DISPATCHER.SOURCE.CSV.HELP')
              }}
            </span>
            <div
              v-if="csvHeaders.length"
              class="mt-4 grid gap-3 border-t border-n-weak pt-4 sm:grid-cols-2"
            >
              <label
                v-for="field in ['name', 'phone_number', 'email', 'identifier']"
                :key="field"
                class="block"
              >
                <span class="mb-2 block text-sm font-medium text-n-slate-12">
                  {{
                    t(
                      `MESSAGE_DISPATCHER.SOURCE.CSV.FIELDS.${field.toUpperCase()}`
                    )
                  }}
                </span>
                <select
                  v-model="csvMapping[field]"
                  class="m-0 w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2.5 text-sm text-n-slate-12"
                >
                  <option value="">
                    {{ t('MESSAGE_DISPATCHER.SOURCE.CSV.IGNORE') }}
                  </option>
                  <option
                    v-for="header in csvHeaders"
                    :key="header"
                    :value="header"
                  >
                    {{ header }}
                  </option>
                </select>
              </label>
              <p class="text-xs text-n-slate-11 sm:col-span-2">
                {{ t('MESSAGE_DISPATCHER.SOURCE.CSV.MAPPING_HELP') }}
              </p>
            </div>
          </div>

          <label v-else-if="selectedSource === 'label'" class="block">
            <span class="mb-2 block text-sm font-medium text-n-slate-12">{{
              t('MESSAGE_DISPATCHER.SOURCE.LABEL.FIELD')
            }}</span>
            <select
              v-model="sourceLabel"
              class="m-0 w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2.5 text-sm text-n-slate-12"
            >
              <option value="">
                {{ t('MESSAGE_DISPATCHER.SOURCE.SELECT') }}
              </option>
              <option
                v-for="label in labels"
                :key="label.id"
                :value="label.title"
              >
                {{ label.title }}
              </option>
            </select>
          </label>

          <label v-else-if="selectedSource === 'inbox'" class="block">
            <span class="mb-2 block text-sm font-medium text-n-slate-12">{{
              t('MESSAGE_DISPATCHER.SOURCE.INBOX.FIELD')
            }}</span>
            <select
              v-model="sourceInboxId"
              class="m-0 w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2.5 text-sm text-n-slate-12"
            >
              <option value="">
                {{ t('MESSAGE_DISPATCHER.SOURCE.SELECT') }}
              </option>
              <option
                v-for="inbox in inboxes"
                :key="inbox.id"
                :value="String(inbox.id)"
              >
                {{ inbox.name }}
              </option>
            </select>
          </label>

          <div v-else class="grid gap-4 md:grid-cols-2">
            <label class="block">
              <span class="mb-2 block text-sm font-medium text-n-slate-12">{{
                t('MESSAGE_DISPATCHER.SOURCE.KANBAN.FUNNEL')
              }}</span>
              <select
                v-model="sourceFunnelId"
                class="m-0 w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2.5 text-sm text-n-slate-12"
                @change="sourceStage = ''"
              >
                <option value="">
                  {{ t('MESSAGE_DISPATCHER.SOURCE.SELECT') }}
                </option>
                <option
                  v-for="funnel in funnels"
                  :key="funnel.id"
                  :value="String(funnel.id)"
                >
                  {{ funnel.name }}
                </option>
              </select>
            </label>
            <label class="block">
              <span class="mb-2 block text-sm font-medium text-n-slate-12">{{
                t('MESSAGE_DISPATCHER.SOURCE.KANBAN.STAGE')
              }}</span>
              <select
                v-model="sourceStage"
                :disabled="!sourceFunnelId"
                class="m-0 w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2.5 text-sm text-n-slate-12 disabled:opacity-50"
              >
                <option value="">
                  {{ t('MESSAGE_DISPATCHER.SOURCE.SELECT') }}
                </option>
                <option
                  v-for="stage in funnelStages"
                  :key="stage.id"
                  :value="stage.id"
                >
                  {{ stage.name }}
                </option>
              </select>
            </label>
          </div>
        </div>

        <div class="mt-4 flex justify-end">
          <Button
            variant="solid"
            color="blue"
            size="md"
            icon="i-lucide-arrow-right"
            :disabled="!canLoadSource"
            :is-loading="loadingList"
            @click="loadSourceRecipients"
          >
            {{ t('MESSAGE_DISPATCHER.SOURCE.CONTINUE') }}
          </Button>
        </div>
      </section>

      <div
        v-else
        class="grid min-h-0 flex-1 gap-4 overflow-y-auto lg:grid-cols-[minmax(0,1fr)_minmax(28rem,34rem)] lg:overflow-hidden"
      >
        <section class="flex min-h-[24rem] flex-col overflow-hidden lg:min-h-0">
          <div class="flex flex-wrap items-center gap-3 pb-3 pr-1">
            <div class="flex min-h-8 min-w-0 flex-1 items-center gap-2">
              <h4
                class="mb-0 text-base font-semibold leading-5 text-n-slate-12 truncate"
              >
                {{ t('MESSAGE_DISPATCHER.SELECT_CONVERSATIONS') }}
              </h4>
              <span
                class="inline-flex items-center rounded-full border border-n-weak bg-n-alpha-1 px-1.5 py-0.5 text-[10px] font-semibold text-n-slate-11 shrink-0"
              >
                {{ selectedIds.size }}/{{ totalConversations }}
              </span>
            </div>
          </div>

          <div class="min-h-0 flex-1 pt-4">
            <div
              v-if="loadingList && conversations.length === 0"
              class="flex justify-center py-8"
            >
              <span class="loading-spinner" />
            </div>
            <div
              v-else-if="selectedSource === 'csv' && csvPreviewRows.length"
              class="flex h-full min-h-0 flex-col overflow-hidden rounded-xl border border-n-weak bg-n-solid-1"
            >
              <div class="border-b border-n-weak px-4 py-3">
                <div class="flex flex-wrap items-center justify-between gap-3">
                  <h5 class="text-sm font-semibold text-n-slate-12">
                  {{ t('MESSAGE_DISPATCHER.SOURCE.CSV.PREVIEW_TITLE') }}
                  </h5>
                  <label class="flex items-center gap-2 text-xs font-medium text-n-slate-11">
                    <input
                      type="checkbox"
                      :checked="allCsvRowsSelected"
                      class="h-4 w-4 rounded border-n-strong text-n-brand"
                      @change="selectAllCsvRows"
                    />
                    {{ t('MESSAGE_DISPATCHER.SELECT_CONVERSATIONS') }}
                  </label>
                </div>
                <p class="mt-1 text-xs text-n-slate-11">
                  {{ t('MESSAGE_DISPATCHER.SOURCE.CSV.PREVIEW_HELP') }}
                </p>
              </div>
              <div class="min-h-0 flex-1 overflow-auto">
                <table class="w-full text-left text-sm">
                  <thead
                    class="sticky top-0 bg-n-solid-2 text-xs text-n-slate-11"
                  >
                    <tr>
                      <th class="w-10 px-4 py-3" />
                      <th class="px-3 py-3">
                        {{ t('MESSAGE_DISPATCHER.SOURCE.CSV.FIELDS.NAME') }}
                      </th>
                      <th class="px-3 py-3">
                        {{
                          t('MESSAGE_DISPATCHER.SOURCE.CSV.FIELDS.PHONE_NUMBER')
                        }}
                      </th>
                      <th class="px-3 py-3">
                        {{ t('MESSAGE_DISPATCHER.SOURCE.CSV.FIELDS.EMAIL') }}
                      </th>
                      <th class="px-3 py-3">
                        {{ t('MESSAGE_DISPATCHER.SOURCE.CSV.STATUS') }}
                      </th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-n-weak">
                    <tr v-for="item in csvPreviewRows" :key="item.index">
                      <td class="px-4 py-3">
                        <input
                          type="checkbox"
                          :checked="selectedCsvRows.has(item.index)"
                          class="h-4 w-4 rounded border-n-strong text-n-brand"
                          @change="toggleCsvRow(item)"
                        />
                      </td>
                      <td class="max-w-40 truncate px-3 py-3 text-n-slate-12">
                        {{ item.row[csvMapping.name] || '—' }}
                      </td>
                      <td class="px-3 py-3 text-n-slate-11">
                        {{ item.row[csvMapping.phone_number] || '—' }}
                      </td>
                      <td class="max-w-52 truncate px-3 py-3 text-n-slate-11">
                        {{ item.row[csvMapping.email] || '—' }}
                      </td>
                      <td class="px-3 py-3">
                        <span
                          class="inline-flex rounded-full px-2 py-1 text-xs font-medium"
                          :class="
                            item.conversation
                              ? 'bg-emerald-100 text-emerald-700'
                              : 'bg-n-alpha-2 text-n-slate-11'
                          "
                        >
                          {{
                            item.conversation
                              ? t('MESSAGE_DISPATCHER.SOURCE.CSV.MATCHED')
                              : t('MESSAGE_DISPATCHER.SOURCE.CSV.WILL_CREATE')
                          }}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div
              v-else-if="filteredConversations.length > 0"
              class="flex h-full min-h-0 flex-col overflow-hidden"
            >
              <div
                class="mb-3 flex flex-wrap items-center justify-between gap-3 rounded-lg bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-11"
              >
                <label class="flex items-center gap-2 font-medium">
                  <input
                    type="checkbox"
                    :checked="allListedSelected"
                    class="h-4 w-4 rounded border-slate-300 bg-white text-woot-500 focus:ring-woot-500 dark:border-slate-600 dark:bg-slate-800"
                    @change="selectAllListed"
                  />
                  <span>
                    {{ t('MESSAGE_DISPATCHER.SELECT_CONVERSATIONS') }}
                  </span>
                </label>
                <span v-if="loadingList" class="flex items-center gap-2">
                  <span class="loading-spinner !h-3 !w-3" />
                  {{ t('MESSAGE_DISPATCHER.LOADING_CONVERSATIONS') }}
                </span>
              </div>

              <div class="min-h-0 flex-1 overflow-auto pr-1">
                <div class="flex flex-col gap-3">
                  <article
                    v-for="c in paginatedConversations"
                    :key="c.id"
                    class="group flex cursor-pointer gap-3 rounded-lg border border-n-weak bg-n-solid-1 p-4 transition hover:border-n-strong hover:bg-n-alpha-1"
                    :class="{
                      'border-woot-500 bg-woot-50/40 dark:bg-woot-900/10':
                        selectedIds.has(c.id),
                    }"
                    @click="toggleId(c.id)"
                  >
                    <input
                      type="checkbox"
                      :checked="selectedIds.has(c.id)"
                      class="mt-1 h-4 w-4 shrink-0 rounded border-slate-300 bg-white text-woot-500 focus:ring-woot-500 dark:border-slate-600 dark:bg-slate-800"
                      @click.stop="toggleId(c.id)"
                    />

                    <img
                      v-if="conversationThumbnail(c)"
                      :src="conversationThumbnail(c)"
                      :alt="conversationTitle(c)"
                      class="h-11 w-11 shrink-0 rounded-full object-cover"
                    />
                    <div
                      v-else
                      class="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-woot-50 text-base font-semibold text-woot-600"
                    >
                      {{ conversationInitial(c) }}
                    </div>

                    <div class="min-w-0 flex-1">
                      <div
                        class="flex flex-wrap items-start justify-between gap-3"
                      >
                        <div class="min-w-0">
                          <h5
                            class="mb-1 truncate text-sm font-semibold text-n-slate-12"
                          >
                            {{ conversationTitle(c) }}
                          </h5>
                          <div
                            class="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-n-slate-11"
                          >
                            <span class="truncate">
                              {{
                                t(
                                  'KANBAN.BULK_ACTIONS.SEND_MESSAGE.CONVERSATION_ID',
                                  { id: c.id }
                                )
                              }}
                            </span>
                            <span class="truncate">
                              {{ conversationInboxName(c) }}
                            </span>
                          </div>
                        </div>

                        <div
                          class="flex shrink-0 flex-wrap items-center justify-end gap-2 text-xs"
                        >
                          <span
                            v-if="c.priority"
                            class="inline-flex rounded-full px-2 py-0.5 font-semibold"
                            :class="priorityClasses(c.priority)"
                          >
                            {{ priorityLabel(c.priority) }}
                          </span>
                          <span class="text-n-slate-11">
                            <TimeAgo
                              :last-activity-timestamp="c.last_activity_at"
                              :created-at-timestamp="c.created_at"
                            />
                          </span>
                        </div>
                      </div>

                      <div class="mt-3 flex min-w-0 flex-wrap gap-1.5">
                        <span
                          v-for="label in conversationLabels(c)"
                          :key="label"
                          class="max-w-full truncate rounded-full bg-n-alpha-2 px-2 py-0.5 text-[11px] font-medium text-n-slate-11"
                        >
                          {{ label }}
                        </span>
                        <span
                          v-if="!conversationLabels(c).length"
                          class="text-xs text-n-slate-10"
                        >
                          {{ t('MESSAGE_DISPATCHER.EMPTY_FIELD') }}
                        </span>
                      </div>
                    </div>
                  </article>
                </div>
              </div>
              <div
                class="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 px-3 py-2 text-sm text-slate-600 dark:border-slate-700 dark:text-slate-400"
              >
                <span>
                  {{
                    t('MESSAGE_DISPATCHER.PAGINATION_STATUS', {
                      page: conversationPage,
                      total: conversationPageCount,
                    })
                  }}
                </span>
                <div class="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    color="slate"
                    size="sm"
                    :disabled="conversationPage <= 1 || loadingList"
                    @click="previousConversationPage"
                  >
                    {{ t('MESSAGE_DISPATCHER.PREVIOUS_PAGE') }}
                  </Button>
                  <Button
                    variant="ghost"
                    color="slate"
                    size="sm"
                    :disabled="
                      conversationPage >= conversationPageCount || loadingList
                    "
                    @click="nextConversationPage"
                  >
                    {{ t('MESSAGE_DISPATCHER.NEXT_PAGE') }}
                  </Button>
                </div>
              </div>
            </div>
            <div
              v-else-if="!loadingList"
              class="py-6 text-center text-sm text-n-slate-11"
            >
              {{ t('MESSAGE_DISPATCHER.EMPTY_CONVERSATIONS') }}
            </div>
          </div>
        </section>

        <aside class="flex min-h-0 flex-col gap-4 overflow-y-auto">
          <section class="space-y-4">
            <div class="flex items-center justify-between gap-3">
              <div class="min-w-0 flex flex-1 items-start gap-2">
                <div class="min-w-0">
                  <h4 class="text-lg font-semibold text-n-slate-12">
                    {{ t('MESSAGE_DISPATCHER.COMPOSER_TITLE') }}
                  </h4>
                  <p class="text-sm text-n-slate-11">
                    {{ t('MESSAGE_DISPATCHER.COMPOSER_DESCRIPTION') }}
                  </p>
                </div>
              </div>
            </div>

            <div class="space-y-4">
              <label class="block space-y-2">
                <span class="text-sm font-medium text-n-slate-12">
                  {{ t('MESSAGE_DISPATCHER.INBOX_LABEL') }}
                </span>
                <select
                  v-model="selectedDispatchInboxId"
                  class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12"
                  @change="handleDispatchInboxChange"
                >
                  <option value="">{{ t('MESSAGE_DISPATCHER.INBOX_SELECT') }}</option>
                  <option v-for="inbox in inboxes" :key="inbox.id" :value="String(inbox.id)">
                    {{ inbox.name }}
                  </option>
                </select>
                <span v-if="selectedDispatchInbox" class="text-xs text-n-slate-11">
                  {{ selectedDispatchInbox.channel_type || selectedDispatchInbox.channelType }}
                </span>
              </label>
              <div
                v-if="hasSelectedWhatsappCloud"
                class="space-y-3 rounded-xl border border-n-weak bg-n-solid-1 p-4"
              >
                <div>
                  <p class="text-xs uppercase tracking-wide text-n-slate-11">
                    {{ t('MESSAGE_DISPATCHER.WHATSAPP_TEMPLATE_SECTION') }}
                  </p>
                  <h5 class="text-sm font-semibold text-n-slate-12">
                    {{ t('MESSAGE_DISPATCHER.WHATSAPP_TEMPLATE_TITLE') }}
                  </h5>
                  <p class="text-sm text-n-slate-11">
                    {{ t('MESSAGE_DISPATCHER.WHATSAPP_TEMPLATE_DESCRIPTION') }}
                  </p>
                </div>

                <TemplatesPicker
                  v-if="!selectedWhatsappTemplate"
                  :inbox-id="whatsappCloudInboxId"
                  @on-select="handleSelectWhatsappTemplate"
                />
                <div v-else class="space-y-3">
                  <WhatsAppTemplateParser
                    ref="whatsappTemplateParserRef"
                    :template="selectedWhatsappTemplate"
                  />
                  <div class="flex justify-end">
                    <Button
                      variant="ghost"
                      color="slate"
                      size="sm"
                      @click="handleResetWhatsappTemplate"
                    >
                      {{ t('WHATSAPP_TEMPLATES.PARSER.GO_BACK_LABEL') }}
                    </Button>
                  </div>
                </div>
              </div>

              <div
                v-if="hasSelectedNonWhatsappCloud || !hasSelectedWhatsappCloud"
              >
                <div class="editor-wrap w-full">
                  <WootMessageEditor
                    id="message-dispatcher-composer"
                    v-model="composerMessage"
                    class="message-editor min-h-[12rem] [&>div]:px-1"
                    channel-type="Context::Default"
                    enable-variables
                    :focus-on-mount="false"
                    :placeholder="t('MESSAGE_DISPATCHER.COMPOSER_PLACEHOLDER')"
                  />
                </div>
              </div>

              <div
                v-if="hasSelectedNonWhatsappCloud || !hasSelectedWhatsappCloud"
                class="space-y-3"
              >
                <div class="flex items-start justify-between gap-3">
                  <div>
                    <p class="text-xs uppercase tracking-wide text-n-slate-11">
                      {{ t('MESSAGE_DISPATCHER.VARIATIONS_TITLE') }}
                    </p>
                    <p class="text-sm text-n-slate-11">
                      {{ t('MESSAGE_DISPATCHER.VARIATIONS_DESCRIPTION') }}
                    </p>
                  </div>
                  <Button
                    v-if="editingVariationIndex === null"
                    variant="outline"
                    color="blue"
                    size="sm"
                    @click="handleAddVariation"
                  >
                    {{ t('MESSAGE_DISPATCHER.ADD_VARIATION') }}
                  </Button>
                </div>

                <div v-if="messageVariations.length > 0" class="space-y-2">
                  <div
                    v-for="(variation, index) in messageVariations"
                    :key="index"
                    class="flex items-start gap-3 rounded-xl border border-n-weak bg-n-alpha-1 p-3"
                  >
                    <div class="min-w-0 flex-1">
                      <div class="mb-1 text-xs font-semibold text-n-slate-11">
                        {{ t('MESSAGE_DISPATCHER.VARIATION') }} {{ index + 1 }}
                      </div>
                      <p class="mb-0 line-clamp-3 text-sm text-n-slate-12">
                        {{ plainTextFromHtml(variation) }}
                      </p>
                    </div>
                    <div class="flex shrink-0 gap-2">
                      <Button
                        variant="ghost"
                        color="slate"
                        size="xs"
                        @click="handleEditVariation(index)"
                      >
                        {{ t('KANBAN.BULK_ACTIONS.EDIT') }}
                      </Button>
                      <Button
                        variant="ghost"
                        color="ruby"
                        size="xs"
                        @click="handleRemoveVariation(index)"
                      >
                        {{ t('KANBAN.BULK_ACTIONS.DELETE') }}
                      </Button>
                    </div>
                  </div>
                </div>

                <div
                  v-if="editingVariationIndex !== null"
                  class="space-y-3 rounded-xl border border-n-brand bg-n-alpha-1 p-4"
                >
                  <label
                    class="block text-sm font-semibold text-n-slate-12"
                    for="message-dispatcher-variation"
                  >
                    {{
                      editingVariationIndex === messageVariations.length
                        ? t('MESSAGE_DISPATCHER.ADD_VARIATION')
                        : t('MESSAGE_DISPATCHER.EDIT_VARIATION')
                    }}
                  </label>
                  <WootMessageEditor
                    id="message-dispatcher-variation"
                    v-model="variationInput"
                    class="message-editor min-h-[10rem] [&>div]:px-1"
                    channel-type="Context::Default"
                    enable-variables
                    :focus-on-mount="false"
                    :placeholder="t('MESSAGE_DISPATCHER.VARIATION_PLACEHOLDER')"
                  />
                  <div class="flex justify-end gap-2">
                    <Button
                      variant="ghost"
                      color="slate"
                      size="sm"
                      @click="handleCancelVariation"
                    >
                      {{ t('KANBAN.BULK_ACTIONS.CANCEL') }}
                    </Button>
                    <Button
                      variant="solid"
                      color="blue"
                      size="sm"
                      @click="handleSaveVariation"
                    >
                      {{ t('KANBAN.BULK_ACTIONS.SAVE') }}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section
            v-if="!sendNow"
            class="space-y-4 rounded-xl border bg-n-solid-1 p-4 shadow-sm transition-shadow"
            :class="
              highlightedStep === 3
                ? 'border-n-brand ring-2 ring-n-brand'
                : 'border-n-weak'
            "
          >
            <div class="flex items-start justify-between gap-3">
              <div class="min-w-0 flex flex-1 items-start gap-2">
                <span
                  class="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white"
                  :class="{ 'bg-n-brand': !step3Done }"
                  :style="
                    step3Done
                      ? { backgroundColor: STEP_COMPLETED_NUMBER_BG }
                      : {}
                  "
                >
                  <fluent-icon v-if="step3Done" icon="checkmark" size="14" />
                  <template v-else>3</template>
                </span>
                <div class="min-w-0">
                  <p class="text-xs uppercase tracking-wide text-n-slate-11">
                    {{ t('MESSAGE_DISPATCHER.SCHEDULE_SECTION') }}
                  </p>
                  <h4 class="text-lg font-semibold text-n-slate-12">
                    {{ t('MESSAGE_DISPATCHER.SCHEDULE_DATETIME') }}
                  </h4>
                  <p class="text-sm text-n-slate-11">
                    {{ t('MESSAGE_DISPATCHER.SCHEDULE_HELP') }}
                  </p>
                </div>
              </div>
            </div>
            <div>
              <label class="mb-2 block text-sm font-semibold text-n-slate-12">
                {{ t('MESSAGE_DISPATCHER.SCHEDULE_DATETIME') }}
              </label>
              <input
                v-model="scheduledTime"
                type="datetime-local"
                class="w-full rounded-xl border border-n-weak bg-n-solid-1 text-n-slate-12 p-3 text-sm"
              />
            </div>
          </section>
        </aside>
      </div>
    </div>

    <Dialog
      ref="filtersDialogRef"
      width="lg"
      :title="t('MESSAGE_DISPATCHER.FILTERS_MODAL.TITLE')"
      :description="t('MESSAGE_DISPATCHER.FILTERS_MODAL.INTRO')"
      :confirm-button-label="t('MESSAGE_DISPATCHER.FILTERS_MODAL.APPLY')"
      :cancel-button-label="t('MESSAGE_DISPATCHER.FILTERS_MODAL.CLOSE')"
      @confirm="closeFiltersModal"
    >
      <div class="grid gap-4">
        <label class="grid gap-2 text-sm font-medium text-n-slate-12">
          {{ t('MESSAGE_DISPATCHER.FILTERS_MODAL.CONVERSATION') }}
          <input
            v-model="conversationFilters.query"
            type="text"
            class="m-0 w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
            :placeholder="
              t('MESSAGE_DISPATCHER.FILTERS_MODAL.CONVERSATION_PLACEHOLDER')
            "
          />
        </label>

        <label class="grid gap-2 text-sm font-medium text-n-slate-12">
          {{ t('MESSAGE_DISPATCHER.CHANNEL_COLUMN') }}
          <select
            v-model="conversationFilters.channel"
            class="m-0 w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
          >
            <option value="">
              {{ t('MESSAGE_DISPATCHER.FILTERS_MODAL.ALL') }}
            </option>
            <option
              v-for="channel in channelFilterOptions"
              :key="channel"
              :value="channel"
            >
              {{ channel }}
            </option>
          </select>
        </label>

        <label class="grid gap-2 text-sm font-medium text-n-slate-12">
          {{ t('MESSAGE_DISPATCHER.LABELS_COLUMN') }}
          <select
            v-model="conversationFilters.label"
            class="m-0 w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
          >
            <option value="">
              {{ t('MESSAGE_DISPATCHER.FILTERS_MODAL.ALL') }}
            </option>
            <option
              v-for="label in labelFilterOptions"
              :key="label"
              :value="label"
            >
              {{ label }}
            </option>
          </select>
        </label>

        <label class="grid gap-2 text-sm font-medium text-n-slate-12">
          {{ t('MESSAGE_DISPATCHER.PRIORITY_COLUMN') }}
          <select
            v-model="conversationFilters.priority"
            class="m-0 w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
          >
            <option value="">
              {{ t('MESSAGE_DISPATCHER.FILTERS_MODAL.ALL') }}
            </option>
            <option
              v-for="priority in priorityFilterOptions"
              :key="priority"
              :value="priority"
            >
              {{ priorityLabel(priority) }}
            </option>
          </select>
        </label>
      </div>

      <template #footer>
        <div class="flex w-full items-center justify-between gap-3">
          <Button
            variant="ghost"
            color="slate"
            size="sm"
            type="button"
            @click="clearConversationFilters"
          >
            {{ t('MESSAGE_DISPATCHER.FILTERS_MODAL.CLEAR') }}
          </Button>
          <Button
            variant="solid"
            color="blue"
            size="sm"
            type="button"
            @click="closeFiltersModal"
          >
            {{ t('MESSAGE_DISPATCHER.FILTERS_MODAL.APPLY') }}
          </Button>
        </div>
      </template>
    </Dialog>

    <Dialog
      ref="variablesDialogRef"
      width="2xl"
      overflow-y-auto
      :title="t('MESSAGE_DISPATCHER.VARIABLES_MODAL.TITLE')"
      :description="t('MESSAGE_DISPATCHER.VARIABLES_MODAL.INTRO')"
      :show-confirm-button="false"
      :cancel-button-label="t('MESSAGE_DISPATCHER.VARIABLES_MODAL.CLOSE')"
    >
      <div
        class="flex flex-col gap-6 max-h-[min(70vh,28rem)] overflow-y-auto pe-1"
      >
        <div class="space-y-3">
          <h4 class="text-sm font-semibold text-n-slate-12 mb-0">
            {{ t('MESSAGE_DISPATCHER.VARIABLES_MODAL.STANDARD_SECTION') }}
          </h4>
          <ul
            class="m-0 list-none divide-y divide-n-weak rounded-xl border border-n-weak bg-n-solid-1"
          >
            <li
              v-for="row in standardVariablesList"
              :key="row.key"
              class="px-4 py-3 flex flex-col gap-1"
            >
              <code class="text-sm font-mono text-n-brand break-all">
                {{ row.key }}
              </code>
              <span class="text-sm text-n-slate-11">{{ row.description }}</span>
            </li>
          </ul>
        </div>

        <div v-if="customVariablesList.length > 0" class="space-y-3">
          <h4 class="text-sm font-semibold text-n-slate-12 mb-0">
            {{ t('MESSAGE_DISPATCHER.VARIABLES_MODAL.CUSTOM_SECTION') }}
          </h4>
          <ul
            class="m-0 list-none divide-y divide-n-weak rounded-xl border border-n-weak bg-n-solid-1"
          >
            <li
              v-for="row in customVariablesList"
              :key="row.key"
              class="px-4 py-3 flex flex-col gap-1"
            >
              <code class="text-sm font-mono text-n-brand break-all">
                {{ row.key }}
              </code>
              <span class="text-sm text-n-slate-11">{{ row.description }}</span>
            </li>
          </ul>
        </div>

        <p class="text-xs text-n-slate-11 mb-0">
          {{ t('MESSAGE_DISPATCHER.VARIABLES_MODAL.EDITOR_HINT') }}
        </p>
      </div>
    </Dialog>
  </div>
</template>
